import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';

import { ApiService } from '../../services/api/api.service';
import * as OrchestrationAssistantActions from '../actions/orchestration-assistant.actions';
import { AlertError } from '../actions/shared.actions';

@Injectable()
export class OrchestrationAssistantEffects {
    constructor(private actions$: Actions, private _api: ApiService) {}

    createSession$ = createEffect(() =>
        this.actions$.pipe(
            ofType(OrchestrationAssistantActions.CreateOrchestrationSession),
            mergeMap(() =>
                this._api.orchestrationAssistant.createSession().pipe(
                    map(res =>
                        OrchestrationAssistantActions.CreateOrchestrationSessionSuccess({
                            session_id: res.session_id,
                        })
                    ),
                    catchError(error =>
                        of(
                            OrchestrationAssistantActions.CreateOrchestrationSessionFailure({ error }),
                            AlertError({ message: 'Failed to create orchestration session' })
                        )
                    )
                )
            )
        )
    );

    sendMessage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(OrchestrationAssistantActions.SendOrchestrationMessage),
            mergeMap(action =>
                this._api.orchestrationAssistant.sendMessage(action.session_id, action.message).pipe(
                    map(res =>
                        OrchestrationAssistantActions.SendOrchestrationMessageSuccess({
                            response: res.response,
                            session_id: action.session_id,
                        })
                    ),
                    catchError(error =>
                        of(
                            OrchestrationAssistantActions.SendOrchestrationMessageFailure({ error }),
                            AlertError({ message: 'Failed to send message' })
                        )
                    )
                )
            )
        )
    );

    loadHistory$ = createEffect(() =>
        this.actions$.pipe(
            ofType(OrchestrationAssistantActions.LoadOrchestrationHistory),
            mergeMap(action =>
                this._api.orchestrationAssistant.getHistory(action.session_id).pipe(
                    map(res =>
                        OrchestrationAssistantActions.LoadOrchestrationHistorySuccess({
                            messages: res.messages || [],
                            session_id: action.session_id,
                        })
                    ),
                    catchError(error =>
                        of(
                            OrchestrationAssistantActions.LoadOrchestrationHistoryFailure({ error }),
                            AlertError({ message: 'Failed to load conversation history' })
                        )
                    )
                )
            )
        )
    );

    deleteSession$ = createEffect(() =>
        this.actions$.pipe(
            ofType(OrchestrationAssistantActions.DeleteOrchestrationSession),
            mergeMap(action =>
                this._api.orchestrationAssistant.deleteSession(action.session_id).pipe(
                    map(() => OrchestrationAssistantActions.DeleteOrchestrationSessionSuccess()),
                    catchError(error =>
                        of(
                            OrchestrationAssistantActions.DeleteOrchestrationSessionFailure({ error }),
                            AlertError({ message: 'Failed to delete session' })
                        )
                    )
                )
            )
        )
    );

    loadSessions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(OrchestrationAssistantActions.LoadOrchestrationSessions),
            mergeMap(() =>
                this._api.orchestrationAssistant.getSessions().pipe(
                    map(res =>
                        OrchestrationAssistantActions.LoadOrchestrationSessionsSuccess({
                            sessions: res || [],
                        })
                    ),
                    catchError(error =>
                        of(
                            OrchestrationAssistantActions.LoadOrchestrationSessionsFailure({ error }),
                            AlertError({ message: 'Failed to load past sessions' })
                        )
                    )
                )
            )
        )
    );

    loadPastSession$ = createEffect(() =>
        this.actions$.pipe(
            ofType(OrchestrationAssistantActions.LoadPastSession),
            mergeMap(action =>
                this._api.orchestrationAssistant.getHistory(action.session_id).pipe(
                    map(res => {
                        // Transform messages from API format to expected format
                        const transformedMessages = ((res || []) as any[]).map(msg => {
                            let content = '';
                            // Handle different possible structures for content
                            if (msg.parts && msg.parts[0]) {
                                if (typeof msg.parts[0] === 'string') {
                                    content = msg.parts[0];
                                } else if (msg.parts[0].text) {
                                    content = msg.parts[0].text;
                                } else if (msg.parts[0].content) {
                                    content = msg.parts[0].content;
                                } else {
                                    content = JSON.stringify(msg.parts[0]);
                                }
                            } else if (msg.content) {
                                content = msg.content;
                            } else if (msg.text) {
                                content = msg.text;
                            }

                            const transformed = {
                                role: msg.role === 'model' ? 'assistant' : msg.role,
                                content: content,
                                timestamp: new Date().toISOString(), // TODO: Use actual timestamp from API if available
                            };

                            return transformed;
                        });

                        return OrchestrationAssistantActions.LoadOrchestrationHistorySuccess({
                            messages: transformedMessages,
                            session_id: action.session_id,
                        });
                    }),
                    catchError(error =>
                        of(
                            OrchestrationAssistantActions.LoadOrchestrationHistoryFailure({ error }),
                            AlertError({ message: 'Failed to load past session' })
                        )
                    )
                )
            )
        )
    );
}
